/* Editor Review Workspace — Feb-2026.
 *
 *  Left  — AI-assisted reader. Chapter selector on top, prose in the
 *          middle, "Suggest edit" button appears next to text selection.
 *  Right — Sticky sidebar with:
 *            • Content-rating card (AI propose → editor confirm)
 *            • Live suggestion list (created inline)
 *            • Decision buttons (Approve / Request Changes / Reject)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, ChevronLeft, ChevronRight, Sparkles, ShieldCheck, XCircle,
  CheckCircle2, AlertTriangle, Highlighter, Send, MessageSquarePlus, Bot,
  Minus, Plus, Bold as BoldIcon, History, GitCompare,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EditorChapterHistoryDrawer } from '@/components/panels/editor/EditorChapterHistoryDrawer';
import { AdminTextDiffDialog } from '@/components/panels/editor/AdminTextDiffDialog';

const API  = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const http = axios.create({ baseURL: API, withCredentials: true });

const CATEGORIES = [
  ['violence',  'Violence'],
  ['sexual',    'Sexual'],
  ['language',  'Language'],
  ['substance', 'Substance'],
  ['self_harm', 'Self-harm'],
  ['themes',    'Themes'],
];
const OVERALLS = ['G', 'PG', 'PG-13', 'R', 'NC-17'];

// FastAPI/Pydantic 422 errors return `detail` as an array of
// {loc, msg, type} objects, not a string — passing that straight to
// toast.error renders "[object Object]". Flatten it to readable text.
function errDetailText(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d) => d?.msg || JSON.stringify(d)).join('; ');
  }
  return detail.msg || JSON.stringify(detail);
}

export default function EditorReviewWorkspace({ reviewId }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [chIdx,    setChIdx]    = useState(0);
  const [rating,   setRating]   = useState(null);
  const [proposed, setProposed] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selection,setSelection]= useState(null);   // {text, chapter_id}
  const [busy,     setBusy]     = useState(false);
  const [decideNotes, setDecideNotes] = useState('');
  const [aiModels,   setAiModels]   = useState([]);
  const [aiModelId,  setAiModelId]  = useState('claude-sonnet-4-5');
  const [aiMode,     setAiMode]     = useState('evaluate');
  const [aiScope,    setAiScope]    = useState('chapter');
  const [aiQuestion, setAiQuestion] = useState('');
  const [aiOutput,   setAiOutput]   = useState('');
  const [aiBusy,     setAiBusy]     = useState(false);
  const [aiNotes,    setAiNotes]    = useState([]);
  const [aiNotesOpen, setAiNotesOpen] = useState(null); // expanded note id
  const [aiBlockProgress, setAiBlockProgress] = useState(null); // {completed, total}
  const [historyOpen, setHistoryOpen] = useState(false);
  const [commitBusy, setCommitBusy] = useState(false);
  // What produced the CURRENT aiOutput — gates Commit/Send so stale
  // output from a different mode, chapter, or scope can never be
  // written into the wrong place (iter: testing_agent found this as a
  // HIGH-severity edge case — e.g. Evaluate critique notes getting
  // committed as chapter prose after switching to Rewrite mode without
  // regenerating, or ch1's rewrite overwriting ch2 after Next chapter).
  const [aiOutputMeta, setAiOutputMeta] = useState(null); // {mode, chapterId, scope}
  // Bumped on every reset/new run so an in-flight stream from a run the
  // user has since navigated away from (mode/chapter/scope changed)
  // stops updating state — otherwise its trailing deltas could land on
  // top of a DIFFERENT chapter's output after Next/Prev mid-generation.
  const aiRunTokenRef = useRef(0);
  const resetAiOutput = () => {
    aiRunTokenRef.current += 1;
    setAiOutput(''); setAiOutputMeta(null); setAiBlockProgress(null);
    setGuidanceDraft(null);
    setCommitDiffOpen(false);
    // Mid-stream mode/chapter/scope switches invalidate the run via the
    // token bump above, but the stale run's own `finally` is now a
    // no-op (token mismatch) — nothing else would ever clear aiBusy,
    // leaving the panel stuck on "Thinking…" forever. Clear it here.
    setAiBusy(false);
  };
  // Chapter navigation normally invalidates any current AI output (it
  // was about this chapter specifically) — EXCEPT a manuscript-wide
  // Evaluate's notes, which aren't tied to any one chapter and should
  // stay usable (incl. the "guide a rewrite" bridge) no matter which
  // chapter you've since navigated to.
  const resetOnChapterNav = () => {
    if (aiOutputMeta?.scope !== 'manuscript') resetAiOutput();
  };
  // "Use these notes to guide a rewrite" — bridges an Evaluate pass's
  // critique into an actual committable Rewrite. null = not in this
  // sub-flow; a string = the editor is drafting/trimming the notes
  // that will become the rewrite's brief before generating.
  const [guidanceDraft, setGuidanceDraft] = useState(null);
  // Local-only reader typography (this admin content-rating tool has no
  // linked author project settings to sync against — persists per
  // browser via sessionStorage so it survives a chapter switch).
  const [fontSizePx, setFontSizePx] = useState(() => {
    const v = parseFloat(sessionStorage.getItem('editorReviewFontSize') || '13.5');
    return Number.isFinite(v) ? Math.max(12, Math.min(32, v)) : 13.5;
  });
  const [bold, setBold] = useState(() => sessionStorage.getItem('editorReviewBold') === '1');
  const setFontSizeClamped = (v) => {
    const next = Math.max(12, Math.min(32, Math.round(v)));
    setFontSizePx(next);
    sessionStorage.setItem('editorReviewFontSize', String(next));
  };
  const toggleBold = (v) => {
    setBold(v);
    sessionStorage.setItem('editorReviewBold', v ? '1' : '0');
  };
  const readerRef = useRef(null);
  const mountedRef = useRef(true);
  useEffect(() => {
    // StrictMode double-invokes effects on mount (mount->cleanup->mount)
    // in dev; without this, the cleanup's `false` from the first pass
    // would stick forever and silently kill every poll before it starts.
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);
  const navigate  = useNavigate();

  const loadAiNotes = useCallback(() => {
    http.get(`/editor/reviews/${reviewId}/ai-notes`)
      .then((r) => setAiNotes(r.data?.notes || []))
      .catch(() => {});
  }, [reviewId]);

  useEffect(() => {
    http.get('/admin/models').then((r) => setAiModels(r.data?.models || [])).catch(() => {});
    loadAiNotes();
  }, [loadAiNotes]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ms, sug] = await Promise.all([
        http.get(`/editor/reviews/${reviewId}/manuscript`),
        http.get(`/editor/reviews/${reviewId}/suggestions`),
      ]);
      setData(ms.data);
      setSuggestions(sug.data?.suggestions || []);
      const existing = ms.data?.book?.content_rating || null;
      const proposal = ms.data?.review?.proposed_rating || null;
      setProposed(proposal);
      setRating(existing || (proposal ? {
        overall:    proposal.overall,
        categories: proposal.categories,
        notes:      proposal.notes,
        locked:     false,
      } : null));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load review.');
    } finally { setLoading(false); }
  }, [reviewId]);

  useEffect(() => { load(); }, [load]);

  const chapters = data?.chapters || [];
  const chapter  = chapters[chIdx];
  // Commit/Send are only ever enabled when the CURRENT aiOutput was
  // produced, for THIS exact chapter, by Rewrite mode at chapter scope —
  // guards against stale Evaluate/Ask output or a different chapter's
  // rewrite getting written into the wrong place.
  const canCommitOrSend = !!aiOutput && !aiBusy && !!aiOutputMeta
    && aiOutputMeta.mode === 'rewrite'
    && aiOutputMeta.scope === 'chapter'
    && aiOutputMeta.chapterId === chapter?.id;
  // Same guard, for the "bridge Evaluate notes into a rewrite" button —
  // offered after a completed chapter-scope Evaluate on the chapter
  // currently open (not stale/different-chapter), OR after a
  // manuscript-wide Evaluate (those notes aren't chapter-bound, so they
  // stay usable on whichever chapter the editor has open right now —
  // the guided rewrite that follows still only ever targets THAT one
  // chapter, same safe single-chapter commit flow as always).
  const canGuideRewrite = !!aiOutput && !aiBusy && !!aiOutputMeta
    && aiOutputMeta.mode === 'evaluate'
    && (aiOutputMeta.scope === 'manuscript'
        || (aiOutputMeta.scope === 'chapter' && aiOutputMeta.chapterId === chapter?.id));

  const proposeRating = async () => {
    setBusy(true);
    try {
      const r = await http.post(`/editor/reviews/${reviewId}/rating/propose`);
      setProposed(r.data.proposal);
      setRating((cur) => cur || {
        overall:    r.data.proposal.overall,
        categories: r.data.proposal.categories,
        notes:      r.data.proposal.notes,
        locked:     false,
      });
      toast.success('AI proposal ready — review, adjust, and Confirm.');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'AI proposal failed.');
    } finally { setBusy(false); }
  };

  const saveRating = async (lock = false) => {
    if (!rating?.overall) {
      toast.error('Pick an overall rating first.');
      return;
    }
    setBusy(true);
    try {
      const r = await http.put(`/editor/reviews/${reviewId}/rating`, {
        overall:    rating.overall,
        categories: rating.categories || {},
        notes:      rating.notes || '',
        lock,
      });
      setRating({ ...(r.data.rating || {}) });
      toast.success(lock ? 'Rating locked.' : 'Rating saved.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally { setBusy(false); }
  };

  const onMouseUp = () => {
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !readerRef.current?.contains(sel.anchorNode)) {
      setSelection(null);
      return;
    }
    const text = (sel.toString() || '').trim();
    if (!text) { setSelection(null); return; }
    if (!chapter) return;
    setSelection({ text: text.slice(0, 2000), chapter_id: chapter.id });
  };

  const createSuggestion = async (kind) => {
    if (!selection) return;
    const note = kind === 'comment'
      ? window.prompt('Comment (visible to author):')
      : null;
    const rewrite = kind === 'edit'
      ? window.prompt('Suggested rewrite (replaces the selected text):')
      : null;
    if (kind === 'comment' && !(note || '').trim()) return;
    if (kind === 'edit'    && !(rewrite || '').trim()) return;
    setBusy(true);
    try {
      const r = await http.post(`/editor/reviews/${reviewId}/suggestions`, {
        chapter_id:    selection.chapter_id,
        kind,
        anchor_text:   selection.text,
        proposed_text: rewrite || undefined,
        note:          note    || undefined,
      });
      setSuggestions((cur) => [r.data, ...cur]);
      toast.success('Suggestion sent to the author.');
      window.getSelection?.().removeAllRanges();
      setSelection(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suggestion failed.');
    } finally { setBusy(false); }
  };

  // 2026-10 — Whole-manuscript AI Assist runs as a background job on
  // the backend (can take minutes for a full novel — many sequential
  // ~9000-word-block AI calls). Poll instead of holding an SSE stream
  // open, which the ingress would eventually cut mid-run with nothing
  // saved. Single-chapter AI Assist below is unchanged (one fast call).
  const pollAiJob = (jobId, myToken) => {
    const poll = async () => {
      if (!mountedRef.current || aiRunTokenRef.current !== myToken) return;
      try {
        const r = await http.get(`/editor/reviews/${reviewId}/ai-assist-job/${jobId}`);
        if (!mountedRef.current || aiRunTokenRef.current !== myToken) return;
        const job = r.data;
        setAiOutput(job.output_text || '');
        setAiBlockProgress({ completed: job.completed_blocks, total: job.total_blocks });
        if (job.status === 'running') {
          setTimeout(poll, 2000);
          return;
        }
        setAiBusy(false);
        if (job.status === 'error' || job.status === 'stalled') {
          toast.error(job.error || 'AI assist failed.');
        }
        if (job.note_id) loadAiNotes();
      } catch (e) {
        if (!mountedRef.current || aiRunTokenRef.current !== myToken) return;
        setAiBusy(false);
        toast.error('Could not reach the AI assist service.');
      }
    };
    poll();
  };

  const runAiAssist = async (guidanceOverride) => {
    // A guided rewrite (bridging an Evaluate pass's notes into a real
    // rewrite) is always mode='rewrite' at chapter scope, regardless of
    // whatever the mode/scope pills currently show — the editor clicked
    // "Generate guided rewrite" from inside the notes editor, not the
    // normal Generate button.
    const effectiveMode  = guidanceOverride ? 'rewrite' : aiMode;
    const effectiveScope = guidanceOverride ? 'chapter' : aiScope;
    if (effectiveMode === 'custom' && !aiQuestion.trim()) {
      toast.error('Type a question first.');
      return;
    }
    // Any fresh run (guided or not) supersedes a draft that was left
    // open from a PRIOR Evaluate — otherwise the guidance editor keeps
    // showing stale notes on top of a new, invisible, streaming result.
    setGuidanceDraft(null);
    aiRunTokenRef.current += 1;
    const myToken = aiRunTokenRef.current;
    setAiBusy(true);
    setAiOutput('');
    setAiBlockProgress(null);
    // Tag this run's output with the mode/chapter/scope that produced it —
    // Commit/Send only ever act on output tagged for the CURRENT context,
    // so switching mode/chapter/scope mid-flight (or after) can never let
    // stale text (e.g. Evaluate notes) get written into chapter prose.
    setAiOutputMeta({ mode: effectiveMode, chapterId: chapter?.id, scope: effectiveScope });
    const chosen = aiModels.find((m) => m.id === aiModelId);
    const payload = {
      chapter_id: effectiveScope === 'chapter' ? chapter?.id : undefined,
      mode: effectiveMode,
      question: effectiveMode === 'custom' ? aiQuestion.trim() : undefined,
      guidance: guidanceOverride || undefined,
      provider: chosen?.provider || 'anthropic',
      model: chosen?.model || 'claude-sonnet-4-5-20250929',
    };

    if (effectiveScope !== 'chapter') {
      try {
        const r = await http.post(`/editor/reviews/${reviewId}/ai-assist`, payload);
        pollAiJob(r.data.id, myToken);
      } catch (e) {
        toast.error(errDetailText(e.response?.data?.detail) || 'AI assist failed.');
        if (aiRunTokenRef.current === myToken) setAiBusy(false);
      }
      return;
    }

    try {
      const resp = await fetch(`${API}/editor/reviews/${reviewId}/ai-assist`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!resp.ok || !resp.body) {
        const errBody = await resp.json().catch(() => null);
        toast.error(errDetailText(errBody?.detail) || 'AI assist failed.');
        if (aiRunTokenRef.current === myToken) setAiBusy(false);
        return;
      }
      const reader = resp.body.getReader();
      const dec = new TextDecoder();
      let buf = '';
      let text = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        if (aiRunTokenRef.current !== myToken) continue; // stale run — drain quietly, don't mutate state
        buf += dec.decode(value, { stream: true });
        const frames = buf.split('\n\n');
        buf = frames.pop();
        for (const f of frames) {
          const line = f.trim();
          if (!line.startsWith('data:')) continue;
          try {
            const p = JSON.parse(line.slice(5).trim());
            if (p.delta) { text += p.delta; setAiOutput(text); }
            if (p.error) toast.error(p.error);
            if (p.done && p.note_id) loadAiNotes();
          } catch { /* ignore malformed frame */ }
        }

      }
    } catch (e) {
      if (aiRunTokenRef.current === myToken) toast.error('Could not reach the AI assist service.');
    } finally {
      if (aiRunTokenRef.current === myToken) setAiBusy(false);
    }
  };

  const sendRewriteAsSuggestion = async () => {
    const targetChapterId = aiOutputMeta?.chapterId;
    const targetChapter = data?.chapters?.find((c) => c.id === targetChapterId);
    if (!aiOutput.trim() || !targetChapter?.content) return;
    setBusy(true);
    try {
      const r = await http.post(`/editor/reviews/${reviewId}/suggestions`, {
        chapter_id:    targetChapterId,
        kind:          'edit',
        anchor_text:   targetChapter.content,
        proposed_text: aiOutput.trim(),
        note:          'AI-assisted full chapter rewrite — reviewed by editor.',
        source:        'ai_proposed',
      });
      setSuggestions((cur) => [r.data, ...cur]);
      toast.success('Rewrite sent to the author as a suggestion.');
      resetAiOutput();
    } catch (e) {
      toast.error(errDetailText(e.response?.data?.detail) || 'Could not send suggestion.');
    } finally { setBusy(false); }
  };

  // Editor hand-edits the AI output above, then commits it straight into
  // the chapter's real content — no author approval round-trip. A
  // version snapshot is taken server-side first so it can be reverted
  // from the chapter's History panel if needed. Always commits to
  // `aiOutputMeta.chapterId` (the chapter the output was generated for),
  // never the CURRENT chapter — those are guaranteed equal by the
  // `canCommitOrSend` gate, but matching on the tagged id keeps this
  // function correct even if that gate is ever loosened.
  //
  // Clicking "Commit to chapter" opens a before/after diff dialog
  // instead of a plain confirm() — the editor sees exactly what changes
  // before it goes live with no author approval step.
  const [commitDiffOpen, setCommitDiffOpen] = useState(false);
  const openCommitDiff = () => {
    const targetChapterId = aiOutputMeta?.chapterId;
    if (!aiOutput.trim() || !targetChapterId) return;
    setCommitDiffOpen(true);
  };

  // Commit summary — concise list of what this session has produced.
  // Shown inside the diff dialog so the editor sees context before confirming.
  const commitSummaryItems = useMemo(() => {
    const items = [];
    const targetChapter = data?.chapters?.find((c) => c.id === aiOutputMeta?.chapterId);
    if (targetChapter) {
      items.push(`Chapter: "${targetChapter.title || `Chapter ${(targetChapter.chapter_index ?? 0) + 1}`}"`);
    }
    if (aiOutputMeta?.mode) {
      const modeLabel = { evaluate: 'Evaluate', rewrite: 'Rewrite', custom: 'Custom ask' }[aiOutputMeta.mode] || aiOutputMeta.mode;
      const scopeLabel = aiOutputMeta.scope === 'manuscript' ? 'whole manuscript' : 'single chapter';
      items.push(`AI mode: ${modeLabel} · ${scopeLabel}`);
    }
    if (rating?.overall) {
      items.push(`Content rating set: ${rating.overall}`);
    }
    const pendingSugs = suggestions.filter((s) => s.status === 'pending').length;
    if (pendingSugs > 0) {
      items.push(`${pendingSugs} pending suggestion${pendingSugs === 1 ? '' : 's'} created for the author`);
    }
    if (aiNotes.length > 0) {
      items.push(`${aiNotes.length} AI note${aiNotes.length === 1 ? '' : 's'} saved to history`);
    }
    return items;
  }, [data, aiOutputMeta, rating, suggestions, aiNotes]);
  const performCommit = async () => {
    const targetChapterId = aiOutputMeta?.chapterId;
    if (!aiOutput.trim() || !targetChapterId) return;
    setCommitBusy(true);
    try {
      const r = await http.post(
        `/editor/reviews/${reviewId}/chapters/${targetChapterId}/commit`,
        { content: aiOutput.trim(), note: `Editor direct commit (${aiOutputMeta?.mode} · ${aiModelId})` },
      );
      setData((cur) => ({
        ...cur,
        chapters: cur.chapters.map((c) => (
          c.id === targetChapterId ? { ...c, content: aiOutput.trim(), words: r.data.words } : c
        )),
      }));
      toast.success('Chapter updated — the change is live (a version snapshot was saved first).');
      resetAiOutput();
      setCommitDiffOpen(false);
    } catch (e) {
      toast.error(errDetailText(e.response?.data?.detail) || 'Could not commit the change.');
    } finally { setCommitBusy(false); }
  };

  // Mirrors the backend GuidanceBody max_length (routes_editor_manuscripts.py).
  // Manuscript-wide Evaluate runs can return ~25k chars across all blocks,
  // so this needs real headroom beyond a single chapter's critique.
  const GUIDANCE_MAX_LENGTH = 40000;

  // Seed the guidance editor with the current Evaluate output so the
  // editor can trim/edit it, then fire a guided Rewrite run using
  // whatever text they leave in place as the brief.
  const startGuidedRewrite = () => setGuidanceDraft(aiOutput);
  const generateGuidedRewrite = () => {
    const g = (guidanceDraft || '').trim();
    if (!g) return;
    if (g.length > GUIDANCE_MAX_LENGTH) {
      toast.error(`Notes are too long (${g.length.toLocaleString()} / ${GUIDANCE_MAX_LENGTH.toLocaleString()} chars) — trim them before generating.`);
      return; // keep the draft in place so nothing is lost
    }
    setGuidanceDraft(null);
    setAiMode('rewrite');   // reflect reality in the mode pills
    setAiScope('chapter');  // a guided rewrite always targets the open chapter
    runAiAssist(g);
  };

  const decide = async (decision) => {
    if (decision !== 'approve' && !decideNotes.trim()) {
      toast.error('Notes are required to Request Changes or Reject.');
      return;
    }
    if (decision === 'approve' && !rating?.overall) {
      toast.error('Save & lock a content rating before approving.');
      return;
    }
    setBusy(true);
    try {
      // Ensure the current rating snapshot is persisted (and locked) first.
      if (decision === 'approve') {
        await saveRating(true);
      }
      const r = await http.post(`/editor/reviews/${reviewId}/decide`, {
        decision, notes: decideNotes,
      });
      toast.success(`Decision recorded — ${r.data.status}`);
      navigate('/editor');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Decide failed.');
    } finally { setBusy(false); }
  };

  if (loading || !data) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading manuscript…
      </div>
    );
  }

  const pendingCount = suggestions.filter((s) => s.status === 'pending').length;

  return (
    <div data-testid="editor-workspace">
      {/* ── Back to queue ─────────────────────────────────────────── */}
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          onClick={() => navigate('/editor')}
          data-testid="editor-back-to-queue"
          className="inline-flex items-center gap-1 rounded-md border border-border bg-card px-3 py-1.5 text-[11.5px] font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          Back to Review queue
        </button>
        <span className="text-[11px] text-muted-foreground truncate">
          {data.book?.title && <>· reviewing <span className="font-medium text-foreground">{data.book.title}</span></>}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
      {/* ── Reader pane ────────────────────────────────────────── */}
      <section className="lg:col-span-3 rounded-md border border-border bg-card">
        <div className="flex items-center justify-between border-b border-border px-3 py-2">
          <div>
            <div className="text-[13px] font-semibold">
              {data.book?.title || 'Manuscript'}
            </div>
            <div className="text-[10.5px] text-muted-foreground">
              Ch. {(chapter?.chapter_index ?? chIdx) + 1} · {chapter?.title || 'Untitled'}
              {' · '}{(chapter?.words || 0).toLocaleString()} words
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setHistoryOpen(true)}
                    data-testid="ed-history-open"
                    title="Chapter history — view & restore past versions"
                    className="rounded-md border border-border p-1 hover:bg-secondary">
              <History className="h-3.5 w-3.5" />
            </button>
            <span className="mx-0.5 h-4 w-px bg-border" />
            <button onClick={() => { setChIdx((i) => Math.max(0, i - 1)); resetOnChapterNav(); }}
                    disabled={chIdx <= 0}
                    data-testid="ed-ch-prev"
                    className="rounded-md border border-border p-1 hover:bg-secondary disabled:opacity-40">
              <ChevronLeft className="h-3.5 w-3.5" />
            </button>
            <div className="px-2 text-[11px] text-muted-foreground">{chIdx + 1} / {chapters.length}</div>
            <button onClick={() => { setChIdx((i) => Math.min(chapters.length - 1, i + 1)); resetOnChapterNav(); }}
                    disabled={chIdx >= chapters.length - 1}
                    data-testid="ed-ch-next"
                    className="rounded-md border border-border p-1 hover:bg-secondary disabled:opacity-40">
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <div className="flex items-center gap-1 border-b border-border px-3 py-1.5" data-testid="ed-typo-toolbar">
          <button onClick={() => setFontSizeClamped(fontSizePx - 1)}
                  data-testid="ed-font-size-down" title="Decrease text size"
                  className="rounded-md p-1 hover:bg-secondary">
            <Minus className="h-3 w-3" />
          </button>
          <span className="min-w-[30px] text-center font-mono text-[10.5px] tabular-nums text-muted-foreground"
                data-testid="ed-font-size-display">
            {fontSizePx}px
          </span>
          <button onClick={() => setFontSizeClamped(fontSizePx + 1)}
                  data-testid="ed-font-size-up" title="Increase text size"
                  className="rounded-md p-1 hover:bg-secondary">
            <Plus className="h-3 w-3" />
          </button>
          <span className="mx-1 h-4 w-px bg-border" />
          <button onClick={() => toggleBold(!bold)}
                  data-testid="ed-bold-toggle" title="Toggle bold text" aria-pressed={bold}
                  className={`rounded-md p-1 hover:bg-secondary ${bold ? 'text-primary bg-primary/10' : ''}`}>
            <BoldIcon className="h-3.5 w-3.5" />
          </button>
        </div>
        <div ref={readerRef} onMouseUp={onMouseUp}
             data-testid="ed-reader"
             className="max-h-[70vh] overflow-y-auto p-5 leading-relaxed text-foreground selection:bg-primary/25"
             style={{ fontFamily: 'Cormorant Garamond, Georgia, serif', fontSize: `${fontSizePx}px`, fontWeight: bold ? 700 : 400 }}>
          {chapter?.content
            ? chapter.content.split(/\n{2,}/).map((p, i) => (
                <p key={i} className="mb-3">{p}</p>
              ))
            : <div className="text-muted-foreground">No content yet on this chapter.</div>}
        </div>
        {selection && (
          <div className="sticky bottom-0 border-t border-border bg-background/95 p-2 shadow"
               data-testid="ed-selection-bar">
            <div className="flex items-center gap-2">
              <span className="line-clamp-1 flex-1 text-[11px] italic text-muted-foreground">
                &ldquo;{selection.text}&rdquo;
              </span>
              <button onClick={() => createSuggestion('comment')}
                      disabled={busy}
                      data-testid="ed-suggest-comment"
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-1 text-[11px] hover:bg-secondary">
                <MessageSquarePlus className="h-3 w-3" /> Comment
              </button>
              <button onClick={() => createSuggestion('edit')}
                      disabled={busy}
                      data-testid="ed-suggest-edit"
                      className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[11px] font-semibold text-primary-foreground hover:bg-primary/90">
                <Highlighter className="h-3 w-3" /> Suggest rewrite
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── Sidebar ────────────────────────────────────────────── */}
      <aside className="lg:col-span-2 space-y-3">
        {/* Ask AI — evaluate, rewrite, or ask a custom question */}
        <div className="rounded-md border border-border bg-card p-3" data-testid="ed-ai-assist-card">
          <div className="inline-flex items-center gap-1 text-[13px] font-semibold">
            <Bot className="h-3.5 w-3.5" /> Ask AI
          </div>
          <div className="mt-2 flex flex-wrap gap-1">
            {[['evaluate', 'Evaluate'], ['rewrite', 'Rewrite'], ['custom', 'Ask…']].map(([k, label]) => (
              <button key={k} onClick={() => { setAiMode(k); resetAiOutput(); }}
                      data-testid={`ed-ai-mode-${k}`}
                      className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${
                        aiMode === k
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background hover:bg-secondary'}`}>
                {label}
              </button>
            ))}
          </div>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <select value={aiScope} onChange={(e) => { setAiScope(e.target.value); resetAiOutput(); }} data-testid="ed-ai-scope"
                    className="h-7 rounded-md border border-border bg-background px-1.5 text-[11px]">
              <option value="chapter">Current chapter</option>
              <option value="manuscript">Whole manuscript</option>
            </select>
            {aiModels.length > 0 && (
              <select value={aiModelId} onChange={(e) => setAiModelId(e.target.value)} data-testid="ed-ai-model"
                      className="h-7 rounded-md border border-border bg-background px-1.5 text-[11px]">
                {aiModels.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
              </select>
            )}
          </div>
          {aiMode === 'custom' && (
            <textarea value={aiQuestion} onChange={(e) => setAiQuestion(e.target.value)}
                      rows={2} placeholder="e.g. Does the pacing drag in this chapter?"
                      data-testid="ed-ai-question"
                      className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1 text-[11.5px]" />
          )}
          <button onClick={() => runAiAssist()} disabled={aiBusy}
                  data-testid="ed-ai-run"
                  className="mt-2 inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
            {aiMode === 'rewrite' ? 'Generate rewrite' : aiMode === 'custom' ? 'Ask' : 'Evaluate'}
          </button>
          {guidanceDraft !== null ? (
            <div className="mt-2 rounded-md border border-primary/40 bg-primary/5 p-2">
              <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
                Editable — trim or edit these notes, then generate a rewrite that addresses them
              </div>
              <textarea
                value={guidanceDraft}
                onChange={(e) => setGuidanceDraft(e.target.value)}
                rows={8}
                maxLength={GUIDANCE_MAX_LENGTH}
                data-testid="ed-ai-guidance-draft"
                className="max-h-64 w-full resize-y whitespace-pre-wrap rounded-md border border-dashed border-border bg-background p-2 text-[11.5px] focus:border-primary focus:outline-none"
              />
              <div
                className={`mt-0.5 text-right text-[10px] ${guidanceDraft.length > GUIDANCE_MAX_LENGTH ? 'font-semibold text-destructive' : 'text-muted-foreground'}`}
                data-testid="ed-ai-guidance-char-count"
              >
                {guidanceDraft.length.toLocaleString()} / {GUIDANCE_MAX_LENGTH.toLocaleString()}
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button onClick={generateGuidedRewrite} disabled={aiBusy || !guidanceDraft.trim() || guidanceDraft.length > GUIDANCE_MAX_LENGTH}
                        data-testid="ed-ai-generate-guided-rewrite"
                        className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
                  {aiBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
                  Generate guided rewrite
                </button>
                <button onClick={() => setGuidanceDraft(null)} disabled={aiBusy}
                        data-testid="ed-ai-guidance-cancel"
                        className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-1 text-[10.5px] font-semibold hover:bg-secondary disabled:opacity-50">
                  Cancel
                </button>
              </div>
            </div>
          ) : (aiOutput || aiBusy) && (
            aiBusy ? (
              <div className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap rounded-md border border-dashed border-border bg-secondary/30 p-2 text-[11.5px]"
                   data-testid="ed-ai-output">
                {aiBlockProgress?.total > 1 && (
                  <div className="mb-1.5 text-[10.5px] font-semibold text-primary" data-testid="ed-ai-block-progress">
                    Processing block {Math.min(aiBlockProgress.completed + 1, aiBlockProgress.total)} of {aiBlockProgress.total}…
                  </div>
                )}
                {aiOutput || 'Thinking…'}
              </div>
            ) : (
              <div className="mt-2">
                {canCommitOrSend && (
                  <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Editable — tweak the text below before committing it to the chapter
                  </div>
                )}
                <textarea
                  value={aiOutput}
                  onChange={(e) => setAiOutput(e.target.value)}
                  readOnly={!canCommitOrSend}
                  rows={10}
                  data-testid="ed-ai-output"
                  className="max-h-64 w-full resize-y whitespace-pre-wrap rounded-md border border-dashed border-border bg-secondary/30 p-2 text-[11.5px] focus:border-primary focus:outline-none"
                />
              </div>
            )
          )}
          {canCommitOrSend && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button onClick={openCommitDiff} disabled={commitBusy || busy}
                      data-testid="ed-ai-commit-chapter"
                      className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[10.5px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                {commitBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <GitCompare className="h-3 w-3" />}
                Commit to chapter
              </button>
              <button onClick={sendRewriteAsSuggestion} disabled={busy || commitBusy}
                      data-testid="ed-ai-send-rewrite"
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-1 text-[10.5px] font-semibold hover:bg-secondary disabled:opacity-50">
                <Send className="h-3 w-3" /> Send full rewrite as suggestion
              </button>
            </div>

          )}
          {canGuideRewrite && guidanceDraft === null && (
            <div className="mt-2">
              <button onClick={startGuidedRewrite}
                      data-testid="ed-ai-use-notes-as-guidance"
                      className="inline-flex items-center gap-1 rounded-md border border-primary/50 bg-primary/5 px-2 py-1 text-[10.5px] font-semibold text-primary hover:bg-primary/10">
                <Sparkles className="h-3 w-3" />
                {aiOutputMeta?.scope === 'manuscript'
                  ? `Use these notes to guide a rewrite of “${chapter?.title || `chapter ${chIdx + 1}`}”`
                  : 'Use these notes to guide a rewrite'}
              </button>
            </div>
          )}
        </div>

        {aiNotes.length > 0 && (
          <div className="rounded-md border border-border bg-card p-3" data-testid="ed-ai-notes-card">
            <div className="text-[11px] font-semibold text-muted-foreground">AI notes history ({aiNotes.length})</div>
            <ul className="mt-1.5 max-h-56 space-y-1 overflow-y-auto">
              {aiNotes.map((n) => {
                const open = aiNotesOpen === n.id;
                return (
                  <li key={n.id} data-testid={`ed-ai-note-${n.id}`} className="rounded-md border border-border/70 bg-secondary/20 p-1.5">
                    <button onClick={() => setAiNotesOpen(open ? null : n.id)}
                            data-testid={`ed-ai-note-toggle-${n.id}`}
                            className="flex w-full items-center justify-between gap-2 text-left text-[10.5px]">
                      <span className="font-semibold capitalize">{n.mode}</span>
                      <span className="text-muted-foreground">{new Date(n.created_at).toLocaleString()}</span>
                    </button>
                    {open && (
                      <div className="mt-1 max-h-40 overflow-y-auto whitespace-pre-wrap text-[11px] text-muted-foreground">
                        {n.output_text}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Content rating */}
        <div className="rounded-md border border-border bg-card p-3" data-testid="ed-rating-card">
          <div className="flex items-center justify-between">
            <div className="inline-flex items-center gap-1 text-[13px] font-semibold">
              <ShieldCheck className="h-3.5 w-3.5" /> Content rating
            </div>
            <button onClick={proposeRating} disabled={busy}
                    data-testid="ed-rating-propose"
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-1 text-[10.5px] hover:bg-secondary disabled:opacity-50">
              {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              AI propose
            </button>
          </div>

          <label className="mt-2 block text-[10.5px] uppercase tracking-wider text-muted-foreground">Overall</label>
          <div className="mt-1 flex flex-wrap gap-1">
            {OVERALLS.map((o) => (
              <button key={o}
                      onClick={() => setRating({ ...(rating || {}), overall: o })}
                      data-testid={`ed-rating-overall-${o}`}
                      className={`rounded-full border px-2 py-0.5 text-[10.5px] font-semibold ${
                        rating?.overall === o
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'border-border bg-background hover:bg-secondary'}`}>
                {o}
              </button>
            ))}
          </div>

          <div className="mt-2 space-y-1">
            {CATEGORIES.map(([k, label]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-[11.5px]">{label}</span>
                <div className="flex items-center gap-1">
                  {[0, 1, 2, 3].map((v) => (
                    <button key={v}
                            onClick={() => setRating({
                              ...(rating || {}),
                              categories: { ...(rating?.categories || {}), [k]: v },
                            })}
                            data-testid={`ed-rating-${k}-${v}`}
                            className={`h-5 w-5 rounded-sm border text-[10.5px] font-mono ${
                              (rating?.categories?.[k] ?? -1) === v
                                ? 'border-primary bg-primary text-primary-foreground'
                                : 'border-border bg-background hover:bg-secondary'}`}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <textarea value={rating?.notes || ''}
                    onChange={(e) => setRating({ ...(rating || {}), notes: e.target.value })}
                    rows={2}
                    placeholder="Editor notes on the rating (optional)"
                    data-testid="ed-rating-notes"
                    className="mt-2 w-full rounded-md border border-border bg-background px-2 py-1 text-[11.5px]" />

          {proposed?.evidence?.length ? (
            <details className="mt-2 rounded-sm border border-dashed border-border bg-secondary/30 p-2">
              <summary className="cursor-pointer text-[10.5px] uppercase tracking-wider text-muted-foreground">
                AI evidence ({proposed.evidence.length})
              </summary>
              <ul className="mt-1 space-y-1">
                {proposed.evidence.map((e, i) => (
                  <li key={i} className="text-[11px]">
                    <b>{e.category}</b>: <span className="italic">&ldquo;{e.excerpt}&rdquo;</span>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}

          <div className="mt-2 flex items-center gap-1">
            <button onClick={() => saveRating(false)} disabled={busy}
                    data-testid="ed-rating-save"
                    className="inline-flex items-center gap-1 rounded-md border border-border bg-secondary/60 px-2 py-1 text-[10.5px] hover:bg-secondary disabled:opacity-50">
              Save draft
            </button>
            <button onClick={() => saveRating(true)} disabled={busy}
                    data-testid="ed-rating-lock"
                    className="inline-flex items-center gap-1 rounded-md bg-primary px-2 py-1 text-[10.5px] font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              Save &amp; lock
            </button>
          </div>
        </div>

        {/* Suggestions list */}
        <div className="rounded-md border border-border bg-card p-3" data-testid="ed-suggest-list">
          <div className="mb-1 flex items-center justify-between">
            <div className="text-[13px] font-semibold">Suggestions</div>
            <div className="text-[10.5px] text-muted-foreground">
              {pendingCount} pending · {suggestions.length} total
            </div>
          </div>
          {suggestions.length === 0 ? (
            <div className="text-[11px] text-muted-foreground">
              Select any passage in the manuscript and click <b>Comment</b> or <b>Suggest rewrite</b>.
            </div>
          ) : (
            <ul className="max-h-56 space-y-1.5 overflow-y-auto text-[11.5px]">
              {suggestions.map((s) => (
                <li key={s.id} data-testid={`ed-sug-${s.id}`}
                    className="rounded border border-border bg-secondary/40 p-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">
                    {s.kind} · {s.status}
                  </div>
                  <div className="italic">&ldquo;{s.anchor_text.slice(0, 140)}&rdquo;</div>
                  {s.proposed_text && (
                    <div className="mt-0.5 rounded bg-emerald-500/10 px-1 py-0.5">→ {s.proposed_text.slice(0, 140)}</div>
                  )}
                  {s.note && (
                    <div className="mt-0.5 text-muted-foreground">{s.note.slice(0, 200)}</div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Decision */}
        <div className="rounded-md border border-border bg-card p-3" data-testid="ed-decide-card">
          <div className="mb-1 text-[13px] font-semibold">Decision</div>
          <textarea value={decideNotes}
                    onChange={(e) => setDecideNotes(e.target.value)}
                    rows={3}
                    placeholder="Required when requesting changes or rejecting."
                    data-testid="ed-decide-notes"
                    className="w-full rounded-md border border-border bg-background px-2 py-1 text-[11.5px]" />
          <div className="mt-2 flex flex-wrap gap-1.5">
            <button onClick={() => decide('approve')} disabled={busy}
                    data-testid="ed-decide-approve"
                    className="inline-flex items-center gap-1 rounded-md bg-emerald-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
              <CheckCircle2 className="h-3 w-3" /> Approve &amp; push
            </button>
            <button onClick={() => decide('request_changes')}
                    disabled={busy || !decideNotes.trim()}
                    data-testid="ed-decide-changes"
                    className="inline-flex items-center gap-1 rounded-md bg-orange-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-orange-700 disabled:opacity-50">
              <AlertTriangle className="h-3 w-3" /> Request changes
            </button>
            <button onClick={() => decide('reject')}
                    disabled={busy || !decideNotes.trim()}
                    data-testid="ed-decide-reject"
                    className="inline-flex items-center gap-1 rounded-md bg-rose-600 px-2 py-1 text-[11px] font-semibold text-white hover:bg-rose-700 disabled:opacity-50">
              <XCircle className="h-3 w-3" /> Reject
            </button>
          </div>
        </div>
      </aside>

      <EditorChapterHistoryDrawer
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        http={http}
        reviewId={reviewId}
        chapterId={chapter?.id}
        onRestored={(restored) => {
          setData((cur) => ({
            ...cur,
            chapters: cur.chapters.map((c) => (
              c.id === restored.chapter_id
                ? { ...c, content: restored.content, title: restored.title, words: restored.words }
                : c
            )),
          }));
        }}
      />

      <AdminTextDiffDialog
        open={commitDiffOpen}
        onClose={() => setCommitDiffOpen(false)}
        title="Review before committing — no author approval step"
        leftLabel="Current chapter"
        rightLabel="After commit"
        leftText={data?.chapters?.find((c) => c.id === aiOutputMeta?.chapterId)?.content || ''}
        rightText={aiOutput}
        confirmLabel="Commit to chapter"
        confirmIcon={CheckCircle2}
        confirmBusy={commitBusy}
        onConfirm={performCommit}
        summaryItems={commitSummaryItems}
      />
    </div>
    </div>
  );
}
